/**
 * Performance Logger Utility
 * 
 * Comprehensive logging system for tracking extension performance metrics including:
 * - Function execution timing
 * - Memory usage tracking
 * - DOM operations monitoring
 * - Redux action logging
 * - React render tracking
 * - Aggregated performance reports
 */

export enum PerformanceCategory {
  INIT = 'INIT',
  INJECTION = 'INJECTION',
  DOM = 'DOM',
  REDUX = 'REDUX',
  REACT = 'REACT',
  WHATSAPP = 'WHATSAPP',
  STYLE = 'STYLE',
  MEMORY = 'MEMORY',
}

interface PerformanceLogEntry {
  category: PerformanceCategory;
  operation: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  memoryBefore?: number;
  memoryAfter?: number;
  details?: Record<string, any>;
  error?: Error;
}

interface MemorySnapshot {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  timestamp: number;
}

class PerformanceLogger {
  private static instance: PerformanceLogger;
  private logs: Map<string, PerformanceLogEntry> = new Map();
  private completedLogs: PerformanceLogEntry[] = [];
  private startTime: number;
  private groupStack: string[] = [];
  private enabled: boolean = false;
  private memorySnapshots: MemorySnapshot[] = [];

  private constructor() {
    this.startTime = performance.now();
    this.logSystemInfo();
  }

  public static getInstance(): PerformanceLogger {
    if (!PerformanceLogger.instance) {
      PerformanceLogger.instance = new PerformanceLogger();
    }
    return PerformanceLogger.instance;
  }

  /**
   * Log system information on initialization
   */
  private logSystemInfo(): void {
    console.log('[PerformanceLogger] 🚀 Performance logging initialized');
    console.log('[PerformanceLogger] System Info:', {
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      performanceSupported: 'performance' in window,
      memorySupported: 'memory' in performance,
    });
    
    this.takeMemorySnapshot('initial');
  }

  /**
   * Enable or disable logging
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    console.log(`[PerformanceLogger] Logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Whether logging is currently enabled
   */
  public get isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current memory usage
   */
  private getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize / (1024 * 1024); // Convert to MB
    }
    return undefined;
  }

  /**
   * Take a memory snapshot
   */
  public takeMemorySnapshot(label: string): MemorySnapshot | null {
    if (!this.enabled) return null;
    if (!('memory' in performance)) {
      return null;
    }

    const memory = (performance as any).memory;
    const snapshot: MemorySnapshot = {
      usedJSHeapSize: memory.usedJSHeapSize / (1024 * 1024), // MB
      totalJSHeapSize: memory.totalJSHeapSize / (1024 * 1024), // MB
      jsHeapSizeLimit: memory.jsHeapSizeLimit / (1024 * 1024), // MB
      timestamp: performance.now(),
    };

    this.memorySnapshots.push(snapshot);

    console.log(`[PerformanceLogger][MEMORY] 📸 Snapshot: ${label}`, {
      used: `${snapshot.usedJSHeapSize.toFixed(2)}MB`,
      total: `${snapshot.totalJSHeapSize.toFixed(2)}MB`,
      limit: `${snapshot.jsHeapSizeLimit.toFixed(2)}MB`,
      utilization: `${((snapshot.usedJSHeapSize / snapshot.jsHeapSizeLimit) * 100).toFixed(2)}%`,
    });

    return snapshot;
  }

  /**
   * Start timing an operation
   */
  public start(
    category: PerformanceCategory,
    operation: string,
    details?: Record<string, any>
  ): string {
    if (!this.enabled) return '';

    const id = `${category}_${operation}_${Date.now()}_${Math.random()}`;
    const memoryBefore = this.getMemoryUsage();

    const entry: PerformanceLogEntry = {
      category,
      operation,
      startTime: performance.now(),
      memoryBefore,
      details,
    };

    this.logs.set(id, entry);

    // Log start with indent based on group depth
    const indent = '  '.repeat(this.groupStack.length);
    console.log(
      `${indent}[PerformanceLogger][${category}] ⏱️  START: ${operation}`,
      details || ''
    );

    return id;
  }

  /**
   * End timing an operation
   */
  public end(id: string, additionalDetails?: Record<string, any>): void {
    if (!this.enabled || !id) return;

    const entry = this.logs.get(id);
    if (!entry) {
      console.warn(`[PerformanceLogger] No entry found for id: ${id}`);
      return;
    }

    entry.endTime = performance.now();
    entry.duration = entry.endTime - entry.startTime;
    entry.memoryAfter = this.getMemoryUsage();

    if (additionalDetails) {
      entry.details = { ...entry.details, ...additionalDetails };
    }

    // Calculate memory delta
    const memoryDelta =
      entry.memoryAfter !== undefined && entry.memoryBefore !== undefined
        ? entry.memoryAfter - entry.memoryBefore
        : undefined;

    // Log completion with indent based on group depth
    const indent = '  '.repeat(this.groupStack.length);
    const memoryInfo =
      memoryDelta !== undefined
        ? ` | Memory Δ: ${memoryDelta > 0 ? '+' : ''}${memoryDelta.toFixed(2)}MB`
        : '';
    const detailsInfo = additionalDetails ? ` | ${JSON.stringify(additionalDetails)}` : '';

    const durationColor = entry.duration > 1000 ? '🔴' : entry.duration > 100 ? '🟡' : '🟢';

    console.log(
      `${indent}[PerformanceLogger][${entry.category}] ${durationColor} END: ${entry.operation} | Duration: ${entry.duration.toFixed(2)}ms${memoryInfo}${detailsInfo}`
    );

    // Move to completed logs
    this.completedLogs.push(entry);
    this.logs.delete(id);

    // Warn about slow operations
    if (entry.duration > 1000) {
      console.warn(
        `[PerformanceLogger][${entry.category}] ⚠️  SLOW OPERATION (>${entry.duration.toFixed(0)}ms): ${entry.operation}`
      );
    }
  }

  /**
   * Mark an operation (instant event with no duration)
   */
  public mark(
    category: PerformanceCategory,
    operation: string,
    details?: Record<string, any>
  ): void {
    if (!this.enabled) return;

    const memory = this.getMemoryUsage();
    const memoryInfo = memory !== undefined ? ` | Memory: ${memory.toFixed(2)}MB` : '';
    const detailsInfo = details ? ` | ${JSON.stringify(details)}` : '';

    const indent = '  '.repeat(this.groupStack.length);
    console.log(
      `${indent}[PerformanceLogger][${category}] 📍 MARK: ${operation}${memoryInfo}${detailsInfo}`
    );

    const entry: PerformanceLogEntry = {
      category,
      operation,
      startTime: performance.now(),
      endTime: performance.now(),
      duration: 0,
      memoryBefore: memory,
      details,
    };

    this.completedLogs.push(entry);
  }

  /**
   * Start a console group
   */
  public group(label: string): void {
    if (!this.enabled) return;
    console.group(`[PerformanceLogger] 📂 ${label}`);
    this.groupStack.push(label);
  }

  /**
   * End a console group
   */
  public groupEnd(): void {
    if (!this.enabled) return;
    if (this.groupStack.length > 0) {
      this.groupStack.pop();
    }
    console.groupEnd();
  }

  /**
   * Log an error
   */
  public error(
    category: PerformanceCategory,
    operation: string,
    error: Error,
    details?: Record<string, any>
  ): void {
    if (!this.enabled) return;

    const indent = '  '.repeat(this.groupStack.length);
    console.error(
      `${indent}[PerformanceLogger][${category}] ❌ ERROR in ${operation}:`,
      error,
      details || ''
    );

    const entry: PerformanceLogEntry = {
      category,
      operation,
      startTime: performance.now(),
      error,
      details,
    };

    this.completedLogs.push(entry);
  }

  /**
   * Measure a synchronous function
   */
  public measure<T>(
    category: PerformanceCategory,
    operation: string,
    fn: () => T,
    details?: Record<string, any>
  ): T {
    const id = this.start(category, operation, details);
    try {
      const result = fn();
      this.end(id);
      return result;
    } catch (error) {
      this.error(category, operation, error as Error, details);
      this.end(id, { error: true });
      throw error;
    }
  }

  /**
   * Measure an asynchronous function
   */
  public async measureAsync<T>(
    category: PerformanceCategory,
    operation: string,
    fn: () => Promise<T>,
    details?: Record<string, any>
  ): Promise<T> {
    const id = this.start(category, operation, details);
    try {
      const result = await fn();
      this.end(id);
      return result;
    } catch (error) {
      this.error(category, operation, error as Error, details);
      this.end(id, { error: true });
      throw error;
    }
  }

  /**
   * Get all completed logs
   */
  public getLogs(): PerformanceLogEntry[] {
    return [...this.completedLogs];
  }

  /**
   * Get logs by category
   */
  public getLogsByCategory(category: PerformanceCategory): PerformanceLogEntry[] {
    return this.completedLogs.filter((log) => log.category === category);
  }

  /**
   * Get logs that took longer than threshold (ms)
   */
  public getSlowOperations(thresholdMs: number = 100): PerformanceLogEntry[] {
    return this.completedLogs.filter(
      (log) => log.duration !== undefined && log.duration > thresholdMs
    );
  }

  /**
   * Generate a performance report
   */
  public generateReport(): void {
    console.group('[PerformanceLogger] 📊 PERFORMANCE REPORT');

    const totalTime = performance.now() - this.startTime;
    console.log(`Total Time Since Init: ${totalTime.toFixed(2)}ms`);

    // Summary by category
    const categoryStats = new Map<
      PerformanceCategory,
      { count: number; totalDuration: number; avgDuration: number }
    >();

    Object.values(PerformanceCategory).forEach((category) => {
      const logs = this.getLogsByCategory(category);
      const durations = logs
        .map((log) => log.duration)
        .filter((d): d is number => d !== undefined);

      if (durations.length > 0) {
        const totalDuration = durations.reduce((sum, d) => sum + d, 0);
        categoryStats.set(category, {
          count: logs.length,
          totalDuration,
          avgDuration: totalDuration / durations.length,
        });
      }
    });

    console.log('\n📈 Stats by Category:');
    categoryStats.forEach((stats, category) => {
      console.log(
        `  ${category}: ${stats.count} operations | Total: ${stats.totalDuration.toFixed(2)}ms | Avg: ${stats.avgDuration.toFixed(2)}ms`
      );
    });

    // Slow operations
    const slowOps = this.getSlowOperations(100);
    if (slowOps.length > 0) {
      console.log('\n⚠️  Slow Operations (>100ms):');
      slowOps
        .sort((a, b) => (b.duration || 0) - (a.duration || 0))
        .slice(0, 10)
        .forEach((log) => {
          console.log(
            `  ${log.category} | ${log.operation}: ${log.duration?.toFixed(2)}ms`
          );
        });
    }

    // Memory summary
    if (this.memorySnapshots.length > 0) {
      console.log('\n💾 Memory Snapshots:');
      const first = this.memorySnapshots[0];
      const last = this.memorySnapshots[this.memorySnapshots.length - 1];
      const delta = last.usedJSHeapSize - first.usedJSHeapSize;

      console.log(`  Initial: ${first.usedJSHeapSize.toFixed(2)}MB`);
      console.log(`  Current: ${last.usedJSHeapSize.toFixed(2)}MB`);
      console.log(`  Delta: ${delta > 0 ? '+' : ''}${delta.toFixed(2)}MB`);
      console.log(
        `  Peak: ${Math.max(...this.memorySnapshots.map((s) => s.usedJSHeapSize)).toFixed(2)}MB`
      );
    }

    // DOM statistics
    const domStats = {
      totalElements: document.getElementsByTagName('*').length,
      shadowRoots: document.querySelectorAll('*').length, // Approximation
      eventListeners: 'getEventListeners' in window ? 'Available' : 'Not available',
    };

    console.log('\n🌐 DOM Statistics:');
    console.log(`  Total Elements: ${domStats.totalElements}`);

    console.groupEnd();
  }

  /**
   * Export logs as JSON
   */
  public exportLogs(): string {
    return JSON.stringify(
      {
        startTime: this.startTime,
        totalDuration: performance.now() - this.startTime,
        logs: this.completedLogs,
        memorySnapshots: this.memorySnapshots,
      },
      null,
      2
    );
  }

  /**
   * Clear all logs
   */
  public clear(): void {
    this.logs.clear();
    this.completedLogs = [];
    this.memorySnapshots = [];
    this.groupStack = [];
    this.startTime = performance.now();
    console.log('[PerformanceLogger] 🧹 Logs cleared');
  }

  /**
   * Get time since logger initialization
   */
  public getElapsedTime(): number {
    return performance.now() - this.startTime;
  }
}

// Export singleton instance
export const perfLogger = PerformanceLogger.getInstance();

/**
 * Decorator for measuring function execution time
 */
export function measured(category: PerformanceCategory, operationName?: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    const operation = operationName || `${target.constructor.name}.${propertyKey}`;

    descriptor.value = function (...args: any[]) {
      return perfLogger.measure(category, operation, () => originalMethod.apply(this, args));
    };

    return descriptor;
  };
}

/**
 * Decorator for measuring async function execution time
 */
export function measuredAsync(category: PerformanceCategory, operationName?: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    const operation = operationName || `${target.constructor.name}.${propertyKey}`;

    descriptor.value = async function (...args: any[]) {
      return perfLogger.measureAsync(category, operation, () =>
        originalMethod.apply(this, args)
      );
    };

    return descriptor;
  };
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).__perfLogger = perfLogger;
  console.log('[PerformanceLogger] 🔍 Access logger via window.__perfLogger');
  console.log('[PerformanceLogger] 💡 Use __perfLogger.generateReport() to see summary');
}

